import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home,
  User,
  FileText,
  Settings,
  LogOut,
  HelpCircle,
  Menu,
  X
} from 'lucide-react';
import { Button } from './ui/button';

const ClientSidebar = ({ isOpen: controlledIsOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  
  const handleToggle = () => {
    if (isControlled && onToggle) {
      onToggle();
    } else {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const menuItems = [
    { icon: Home, label: 'Accueil', path: '/client/dashboard' },
    { icon: User, label: 'Données personnelles', path: '/client/personal-data' },
    { icon: Settings, label: 'Mes préférences', path: '/client/preferences' },
    { icon: FileText, label: 'Documents & e-relevés', path: '/client/documents' },
    { icon: HelpCircle, label: 'Aide et contact', path: '/client/help-contact' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    // Close menu on mobile after clicking a link
    if (window.innerWidth < 768) {
      if (isControlled && onToggle) {
        onToggle();
      } else {
        setInternalIsOpen(false);
      }
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={handleToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed md:static inset-y-0 left-0 z-50
        w-64 bg-white min-h-screen border-r border-gray-100 flex flex-col shadow-sm
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo Header - Black to match logo background */}
        <div className="p-4 bg-black">
          <div className="flex items-center justify-between">
            <Link to="/client/dashboard" onClick={handleNavClick}>
              <img 
                src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
                alt="Younited"
                className="h-10 object-contain"
              />
            </Link>
            {/* Close button for mobile */}
            <button 
              className="md:hidden text-white p-1"
              onClick={handleToggle}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* User Welcome */}
        <div className="p-6 border-b border-gray-100">
          <p className="text-gray-500 text-sm">Bienvenue,</p>
          <p className="text-gray-900 font-bold text-lg">{user?.firstName} {user?.lastName}</p>
        </div>

        {/* Menu */}
        <nav className="flex-1 py-4">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`
                  flex items-center px-6 py-3.5 text-sm font-medium transition-all
                  ${isActive
                    ? 'bg-purple-50 text-purple-700 border-r-4 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-purple-600' : 'text-gray-400'}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-gray-100">
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-600 hover:bg-red-50 hover:text-red-600"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5 mr-3" />
            Déconnexion
          </Button>
        </div>
      </div>
    </>
  );
};

export default ClientSidebar;
